import { getStoredToken } from './auth'
import type { Debt } from '@/views/debt/debt.types'
const API_BASE_URL = import.meta.env.VITE_DAPI_URL || ''

import { LoanTermsLengthInMonths, MortgageInterestTerm } from '@/types/interest.types'

import { notify } from '@staff/ui'

export type LoanInterestTermEffectiveFrom = 'tomorrow' | 'nextChangeDate'

export type LoanTermsPayload = {
  lengthInMonths: LoanTermsLengthInMonths
  discounts: Array<{ bps: number; reason?: string }>
}

export type BindLoanInterestTermPayload = {
  interestTerm: {
    nextInterestTerm: MortgageInterestTerm
    effectiveFrom: LoanInterestTermEffectiveFrom
  }
  loanTerms?: LoanTermsPayload
}

export type ChangeDebtAmortizationPayload = {
  loans: Array<{
    id: string
    amortizationPerPeriod: number
  }>
}

const getAuthorizationHeader = () => {
  const token = getStoredToken()

  if (!token || !('jwt' in token) || !token.jwt) {
    throw new Error('Missing authentication token.')
  }

  return { authorization: `bearer ${token.jwt}` }
}

export const fetchDebtById = async (debtId: string): Promise<Debt> => {
  const response = await fetch(`${API_BASE_URL}/borgo/distributor/debts/${debtId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
  })

  if (!response.ok) {
    throw new Error(`Kunde inte hämta bolån med id ${debtId}`)
  }

  return await response.json()
}

export const uploadDebtDocument = async (
  debtId: string,
  payload: { file: File; type: string; userId?: string },
): Promise<{ documentId: string }> => {
  const formData = new FormData()
  formData.append('file', payload.file)
  formData.append('type', payload.type)
  if (payload.userId) {
    formData.append('userId', payload.userId)
  }

  const response = await fetch(`${API_BASE_URL}/borgo/distributor/debts/${debtId}/documents`, {
    method: 'POST',
    credentials: 'include',
    headers: getAuthorizationHeader(),
    body: formData,
  })

  if (!response.ok) {
    const message = `Kunde inte ladda upp dokument för bolånet`

    notify({
      type: 'error',
      title: message,
      dismissable: true,
    })

    throw new Error(message)
  }

  return await response.json()
}

const getErrorMessage = async (response: Response, fallbackMessage: string) => {
  try {
    const data = (await response.json()) as { message?: string; error?: string }
    return data.message || data.error || fallbackMessage
  } catch {
    return fallbackMessage
  }
}

// Answers 204 with no body, so there is nothing to parse and nothing to return.
export const bindLoanInterestTerm = async (
  debtId: string,
  loanId: string,
  payload: BindLoanInterestTermPayload,
): Promise<void> => {
  const { nextInterestTerm, effectiveFrom } = payload.interestTerm
  const endpoint = effectiveFrom === 'tomorrow' ? 'interest-change-tomorrow' : 'interest-change-next-period'
  const response = await fetch(`${API_BASE_URL}/borgo/distributor/debts/${debtId}/loans/${loanId}/${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
    body: JSON.stringify({ nextInterestTerm, loanTerms: payload.loanTerms }),
  })

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, 'Kunde inte binda räntan.'))
  }
}

// Answers 204 with no body, so there is nothing to parse and nothing to return.
export const changeLoanAmortization = async (debtId: string, payload: ChangeDebtAmortizationPayload): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/borgo/distributor/debts/${debtId}/change-amortization`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, 'Kunde inte ändra månadsamorteringen.'))
  }
}
